import {reactive} from 'vue';

const FINISH_DISPLAY_MS = 1000;

function wait(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise(resolve => {
        const onAbort = () => {
            clearTimeout(timer);
            resolve();
        };

        const timer = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
        }, ms);

        signal.addEventListener('abort', onAbort, {once: true});
    });
}

export interface ProgressHandle {
    signal: AbortSignal;
    setTotal: (value: number) => void;
    tick: (count?: number) => void;
}

export interface ProgressState {
    isRunning: boolean;
    done: number;
    total: number;
    error: unknown;
}

export interface ProgressTask {
    state: ProgressState;
    run: (job: (handle: ProgressHandle) => Promise<void>) => Promise<boolean>;
    cancel: () => void;
}

export function createProgressTask(): ProgressTask {
    const state: ProgressState = reactive({
        isRunning: false,
        done: 0,
        total: 0,
        error: null,
    });

    let controller: AbortController | null = null;

    async function run(job: (handle: ProgressHandle) => Promise<void>): Promise<boolean> {
        controller?.abort();
        controller = new AbortController();

        const signal = controller.signal;

        Object.assign(state, {isRunning: true, done: 0, total: 0, error: null});

        try {
            await job({
                signal,
                setTotal: (value: number) => {
                    if (!signal.aborted) {
                        state.total = value;
                    }
                },
                tick: (count: number = 1) => {
                    if (!signal.aborted) {
                        state.done += count;
                    }
                },
            });

            if (signal.aborted) {
                return false;
            }

            state.total = Math.max(state.done, 1);
            state.done = state.total;

            await wait(FINISH_DISPLAY_MS, signal);

            return !signal.aborted;
        } catch (error) {
            if (signal.aborted) {
                return false;
            }

            state.error = error;

            throw error;
        } finally {
            if (!signal.aborted) {
                state.isRunning = false;
            }
        }
    }

    function cancel() {
        controller?.abort();
        controller = null;
        state.isRunning = false;
    }

    return {state, run, cancel};
}
